/**
 * Product page: engraving add-on accordion (smooth height), character limit,
 * and add-button UI (disabled when empty; ADDED state + price hidden on confirm — no cart).
 */
(function () {
  const DURATION = 320;
  const EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function cancelPanelAnimations(panel) {
    if (typeof panel.getAnimations !== 'function') return;
    panel.getAnimations().forEach((a) => a.cancel());
  }

  function animateHeight(panel, keyframes, onFinish) {
    cancelPanelAnimations(panel);
    const anim = panel.animate(keyframes, {
      duration: prefersReducedMotion() ? 0 : DURATION,
      easing: EASING,
    });
    anim.onfinish = onFinish;
    return anim;
  }

  function openPanel(header, panel) {
    panel.style.overflow = 'hidden';

    // Measure in an "open" layout state to avoid `scrollHeight` returning 0
    // when the panel starts at `height: 0`.
    panel.style.height = 'auto';
    const end = panel.getBoundingClientRect().height;
    panel.style.height = '0px';

    requestAnimationFrame(() => {
      animateHeight(
        panel,
        [{ height: '0px' }, { height: `${end}px` }],
        () => {
          panel.style.height = 'auto';
          panel.style.overflow = '';
          header.classList.add('is-open');
          header.setAttribute('aria-expanded', 'true');
          panel.setAttribute('aria-hidden', 'false');
        }
      );
    });
  }

  function closePanel(header, panel) {
    panel.style.overflow = 'hidden';
    const start = panel.getBoundingClientRect().height;
    panel.style.height = `${start}px`;

    requestAnimationFrame(() => {
      animateHeight(
        panel,
        [{ height: `${start}px` }, { height: '0px' }],
        () => {
          panel.style.height = '0px';
          panel.style.overflow = 'hidden';
          header.classList.remove('is-open');
          header.setAttribute('aria-expanded', 'false');
          panel.setAttribute('aria-hidden', 'true');
        }
      );
    });
  }

  const ADDED_LABEL = 'ADDED';

  function syncCounter(input, countEl, maxLen) {
    let v = input.value;
    if (v.length > maxLen) {
      v = v.slice(0, maxLen);
      input.value = v;
    }
    countEl.textContent = String(v.length);
  }

  function syncAddButtonState(input, btn) {
    const variantId = btn?.dataset?.variantId;
    const text = (input.value || '').trim();
    if (!variantId) {
      btn.disabled = true;
      return;
    }
    if (btn.classList.contains('is-engraving-committed')) {
      btn.disabled = true;
      return;
    }
    btn.disabled = text.length === 0;
  }

  function resetEngravingAdded(btn, input, labelEl, sepEl, priceEl) {
    btn.classList.remove('is-engraving-committed');
    input.classList.remove('is-engraving-added');
    labelEl.textContent = btn.dataset.defaultLabel || '';
    sepEl.classList.remove('hidden');
    priceEl.classList.remove('hidden');
  }

  function applyEngravingAdded(input, labelEl, sepEl, priceEl) {
    input.classList.add('is-engraving-added');
    labelEl.textContent = ADDED_LABEL;
    sepEl.classList.add('hidden');
    priceEl.classList.add('hidden');
  }

  function initEngravingAddButton(root, input) {
    const btn = root.querySelector('[data-engraving-add]');
    if (!btn || !input) return;

    const labelEl = btn.querySelector('[data-engraving-btn-label]');
    const sepEl = btn.querySelector('[data-engraving-btn-sep]');
    const priceEl = btn.querySelector('[data-engraving-price]');
    if (!labelEl || !sepEl || !priceEl) return;

    if (!btn.dataset.defaultLabel?.trim()) {
      btn.dataset.defaultLabel = labelEl.textContent.trim();
    }

    syncAddButtonState(input, btn);

    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      const text = (input.value || '').trim();
      if (text.length === 0) return;

      btn.classList.add('is-engraving-committed');
      applyEngravingAdded(input, labelEl, sepEl, priceEl);
      syncAddButtonState(input, btn);
    });
  }

  function init(root) {
    if (root.dataset.engravingInitialized === 'true') return;
    root.dataset.engravingInitialized = 'true';

    const header = root.querySelector('[data-engraving-toggle]');
    const panel = root.querySelector('[data-engraving-panel]');
    const input = root.querySelector('[data-engraving-input]');
    const countEl = root.querySelector('[data-engraving-count]');
    const maxLen = parseInt(root.dataset.maxLen, 10);

    if (!header || !panel) return;

    panel.setAttribute('aria-hidden', 'true');

    header.addEventListener('click', () => {
      const isOpen = header.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        closePanel(header, panel);
      } else {
        openPanel(header, panel);
      }
    });

    const addBtn = root.querySelector('[data-engraving-add]');
    const labelEl = addBtn?.querySelector('[data-engraving-btn-label]');
    const sepEl = addBtn?.querySelector('[data-engraving-btn-sep]');
    const priceEl = addBtn?.querySelector('[data-engraving-price]');

    const onInputOrPaste = () => {
      if (addBtn && labelEl && sepEl && priceEl && addBtn.classList.contains('is-engraving-committed')) {
        resetEngravingAdded(addBtn, input, labelEl, sepEl, priceEl);
      }
      if (input && countEl) syncCounter(input, countEl, maxLen);
      if (addBtn && input) syncAddButtonState(input, addBtn);
    };

    if (input && countEl) {
      input.addEventListener('input', onInputOrPaste);
      input.addEventListener('paste', () => {
        requestAnimationFrame(onInputOrPaste);
      });
    }

    initEngravingAddButton(root, input);
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-engraving-addon]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
